import { createFileRoute, Link } from "@tanstack/react-router";
import { RotateCcw } from "lucide-react";
import { Layout } from "@/components/site/Layout";

export const Route = createFileRoute("/refund-policy")({
  head: () => ({
    meta: [
      { title: "Cancellation & Refund Policy | XAU Pro Academy" },
      {
        name: "description",
        content:
          "How to cancel an XAU Pro Academy subscription, when refunds apply, and how billing is handled through our payment processor, Stripe.",
      },
      { property: "og:title", content: "Cancellation & Refund Policy | XAU Pro Academy" },
      {
        property: "og:description",
        content:
          "Cancel anytime from your account. Clear refund eligibility for new subscribers and verified access failures.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://xauproacademy.com/refund-policy" }],
  }),
  component: RefundPolicyPage,
});

const linkCls = "text-foreground underline-offset-2 hover:underline";

function RefundPolicyPage() {
  return (
    <Layout>
      <section className="container mx-auto max-w-3xl px-4 py-16">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gold-muted text-gold">
          <RotateCcw className="h-7 w-7" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold md:text-4xl">
          Cancellation &amp; Refund Policy
        </h1>
        <p className="mt-3 text-muted-foreground">
          How cancellations, renewals and refunds work for XAU Pro Academy memberships.
        </p>
        <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
          Last Updated: October 2026
        </p>

        <div className="mt-10 space-y-8 text-sm leading-relaxed text-foreground/90">
          <section>
            <h2 className="font-display text-lg font-semibold">1. Billing Provider</h2>
            <p className="mt-2 text-muted-foreground">
              Our subscription payments are processed through{" "}
              <strong className="text-foreground">Stripe</strong>, our payment
              service provider. All billing and payment related customer service
              inquiries are handled by our support team.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">2. Cancel Anytime</h2>
            <p className="mt-2 text-muted-foreground">
              Monthly and annual memberships renew automatically until cancelled. You can
              cancel at any time in one step from your Profile page under Membership &amp;
              Billing, or through the Stripe customer portal linked in your receipt email.
              Cancellation stops all future renewals. Your access continues until the end
              of the period already paid.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">3. 72-Hour Refund Window for New Subscribers</h2>
            <p className="mt-2 text-muted-foreground">
              If you are subscribing for the first time, you may request a full refund
              within 72 hours of your initial payment. After a refund is issued, paid
              access ends immediately. This window applies once per customer and does not
              apply to renewals.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">4. Verified Access Failures</h2>
            <p className="mt-2 text-muted-foreground">
              If a confirmed technical fault on our side prevents you from accessing paid
              features and it is not resolved within 48 hours of your report, you may
              request a pro-rated or full refund for the affected period.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">5. Renewals and Partial Periods</h2>
            <p className="mt-2 text-muted-foreground">
              Outside the cases above, renewal charges and partially used periods are not
              refunded, except where required by applicable consumer law. Market closures,
              periods without published setups, and engine pauses triggered by risk
              controls are normal operating conditions and are not grounds for a refund.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">6. How to Request a Refund</h2>
            <p className="mt-2 text-muted-foreground">
              Email{" "}
              <a href="mailto:support@xauproacademy.com" className={linkCls}>
                support@xauproacademy.com
              </a>{" "}
              with the email address on your account, or reply to your Stripe receipt.
              Approved refunds are issued through Stripe to the original payment method,
              usually within 5 to 10 business days. Please contact us before opening a
              dispute with your card issuer so we can resolve the matter directly.
            </p>
          </section>

          <section>
            <h2 className="font-display text-lg font-semibold">7. Related Policies</h2>
            <p className="mt-2 text-muted-foreground">
              This policy forms part of our{" "}
              <Link to="/terms" className={linkCls}>Terms of Service</Link>. XAU Pro
              Academy is operated by Orion AI Technologies - F.Z.C, Ajman Free Zone,
              United Arab Emirates (License No. 58070).
            </p>
          </section>
        </div>
      </section>
    </Layout>
  );
}
